/**
 * Every write the UI can perform.
 *
 * Each function builds a complete record, stamps it with the local clock, and hands it to
 * the store, which updates memory synchronously and queues the write for replication. The UI
 * therefore never awaits a mutation before rendering its result.
 */

import {
  AttendanceState,
  EntityKind,
  MemberRole,
  OverrideKind,
  attendanceMarkId,
  membershipId,
  noteId,
  sessionOverrideId,
  type AttendanceMark,
  type IsoDate,
  type Membership,
  type Note,
  type Person,
  type PersonId,
  type Project,
  type ProjectId,
  type SessionOverride,
  type Weekday,
} from '../../shared/types.js';
import type { PushOp } from '../../shared/sync-protocol.js';
import { isSessionDate, type OverrideIndex } from '../../shared/schedule.js';
import { weekdayOf, withinRange } from '../../shared/dates.js';
import { AppStore, newId } from './store.js';

/** Weekdays a new project checks by default: Monday through Friday. */
const DEFAULT_WEEKDAYS: Weekday[] = [1, 2, 3, 4, 5];

/** Why a write could not be performed. */
export type MutationError =
  | { kind: 'not_found'; entity: string; id: string }
  | { kind: 'invalid'; field: string; message: string };

/** Renders a mutation error as a message for the UI. */
export function describeMutationError(error: MutationError): string {
  switch (error.kind) {
    case 'not_found': return `That ${error.entity} no longer exists.`;
    case 'invalid': return error.message;
  }
}

/** Creates a project with the default weekday schedule and returns its id. */
export async function createProject(store: AppStore, name: string, ownerEmail: string): Promise<ProjectId> {
  const now = Date.now();
  const project: Project = {
    id: newId(),
    name: name.trim(),
    createdAt: now,
    weekdays: DEFAULT_WEEKDAYS,
    termStart: null,
    termEnd: null,
    countExcusedAsPresent: false,
    shareLinkPreview: false,
    updatedAt: now,
    deletedAt: null,
  };
  const membership: Membership = {
    id: membershipId(project.id, ownerEmail),
    projectId: project.id,
    email: ownerEmail.trim().toLowerCase(),
    userId: null,
    role: MemberRole.Owner,
    updatedAt: now,
    deletedAt: null,
  };
  await store.writeMany([
    { kind: EntityKind.Project, record: project },
    { kind: EntityKind.Membership, record: membership },
  ]);
  return project.id;
}

/** Applies `change` to the stored project and replicates it. */
async function updateProject(
  store: AppStore,
  projectId: ProjectId,
  change: Partial<Omit<Project, 'id' | 'updatedAt'>>,
): Promise<void> {
  const existing = store.replica.projects.get(projectId);
  if (existing === undefined) return;
  await store.write({
    kind: EntityKind.Project,
    record: { ...existing, ...change, updatedAt: Date.now() },
  });
}

/** Renames a project. */
export async function renameProject(store: AppStore, projectId: ProjectId, name: string): Promise<void> {
  await updateProject(store, projectId, { name: name.trim() });
}

/** Replaces the weekdays on which sessions recur. */
export async function setWeekdays(store: AppStore, projectId: ProjectId, weekdays: Weekday[]): Promise<void> {
  await updateProject(store, projectId, { weekdays: [...new Set(weekdays)].sort((a, b) => a - b) });
}

/** Sets the term bounds; `null` leaves that end open. */
export async function setTerm(
  store: AppStore,
  projectId: ProjectId,
  termStart: IsoDate | null,
  termEnd: IsoDate | null,
): Promise<void> {
  await updateProject(store, projectId, { termStart, termEnd });
}

/** Chooses whether excused absences count towards the present rate. */
export async function setCountExcusedAsPresent(
  store: AppStore,
  projectId: ProjectId,
  countExcusedAsPresent: boolean,
): Promise<void> {
  await updateProject(store, projectId, { countExcusedAsPresent });
}

/**
 * Chooses whether a link to this project shows its name and roster size in chat previews.
 *
 * Turning this on discloses those two facts to anyone holding the link and to the service
 * rendering the preview, with no sign-in required.
 */
export async function setShareLinkPreview(
  store: AppStore,
  projectId: ProjectId,
  shareLinkPreview: boolean,
): Promise<void> {
  await updateProject(store, projectId, { shareLinkPreview });
}

/** Soft-deletes a project; only an owner's delete is accepted by the server. */
export async function deleteProject(store: AppStore, projectId: ProjectId): Promise<void> {
  await updateProject(store, projectId, { deletedAt: Date.now() });
}

/** Adds someone to a roster and returns their id. */
export async function addPerson(
  store: AppStore,
  projectId: ProjectId,
  name: string,
  note = '',
): Promise<PersonId> {
  const person: Person = {
    id: newId(),
    projectId,
    name: name.trim(),
    note: note.trim(),
    updatedAt: Date.now(),
    deletedAt: null,
  };
  await store.write({ kind: EntityKind.Person, record: person });
  return person.id;
}

/**
 * Adds several people at once, one per non-empty line of `block`.
 *
 * @returns the ids of the people created, in input order.
 */
export async function addPeopleFromLines(
  store: AppStore,
  projectId: ProjectId,
  block: string,
): Promise<PersonId[]> {
  const now = Date.now();
  const people = block
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((name): Person => ({
      id: newId(),
      projectId,
      name,
      note: '',
      updatedAt: now,
      deletedAt: null,
    }));
  await store.writeMany(people.map((person) => ({ kind: EntityKind.Person as const, record: person })));
  return people.map((person) => person.id);
}

/** Edits a person's name and note. */
export async function updatePerson(
  store: AppStore,
  personId: PersonId,
  change: { name?: string; note?: string },
): Promise<void> {
  const existing = store.replica.people.get(personId);
  if (existing === undefined) return;
  await store.write({
    kind: EntityKind.Person,
    record: {
      ...existing,
      name: change.name === undefined ? existing.name : change.name.trim(),
      note: change.note === undefined ? existing.note : change.note.trim(),
      updatedAt: Date.now(),
    },
  });
}

/** Soft-deletes a person, keeping their past marks for historical stats. */
export async function removePerson(store: AppStore, personId: PersonId): Promise<void> {
  const existing = store.replica.people.get(personId);
  if (existing === undefined) return;
  await store.write({
    kind: EntityKind.Person,
    record: { ...existing, deletedAt: Date.now(), updatedAt: Date.now() },
  });
}

/** Invites an email address to a project, or restores a previously removed member. */
export async function inviteMember(
  store: AppStore,
  projectId: ProjectId,
  email: string,
  role: MemberRole = MemberRole.Member,
): Promise<void> {
  const normalized = email.trim().toLowerCase();
  const id = membershipId(projectId, normalized);
  const existing = store.replica.memberships.get(id);
  const membership: Membership = {
    id,
    projectId,
    email: normalized,
    userId: existing?.userId ?? null,
    role,
    updatedAt: Date.now(),
    deletedAt: null,
  };
  await store.write({ kind: EntityKind.Membership, record: membership });
}

/** Revokes a membership. Removing your own is how you leave a project. */
export async function removeMember(store: AppStore, membershipRecordId: string): Promise<void> {
  const existing = store.replica.memberships.get(membershipRecordId);
  if (existing === undefined) return;
  const now = Date.now();
  await store.write({
    kind: EntityKind.Membership,
    record: { ...existing, deletedAt: now, updatedAt: now },
  });
}

/**
 * Turns `date` into a session or removes it, whichever reverses the current state.
 *
 * When the recurring weekday rule already produces the wanted answer the override record is
 * soft-deleted rather than inverted, so the calendar stays free of redundant exceptions.
 */
export async function toggleSessionDate(
  store: AppStore,
  project: Pick<Project, 'id' | 'weekdays' | 'termStart' | 'termEnd'>,
  overrides: OverrideIndex,
  date: IsoDate,
): Promise<void> {
  const id = sessionOverrideId(project.id, date);
  const currentlySession = isSessionDate(project, date, overrides);
  const wantedKind = currentlySession ? OverrideKind.Skipped : OverrideKind.Added;
  const baseline = project.weekdays.includes(weekdayOf(date))
    && withinRange(date, project.termStart, project.termEnd);
  const now = Date.now();
  const existing = store.replica.sessionOverrides.get(id);

  // The wanted state already follows from the schedule, so no exception needs to exist.
  const redundant = (wantedKind === OverrideKind.Added && baseline)
    || (wantedKind === OverrideKind.Skipped && !baseline);

  const record: SessionOverride = {
    id,
    projectId: project.id,
    date,
    kind: wantedKind,
    updatedAt: now,
    deletedAt: redundant ? now : null,
  };
  if (redundant && existing === undefined) return;
  await store.write({ kind: EntityKind.SessionOverride, record });
}

/** Records or clears one person's attendance on one date. */
export async function setMark(
  store: AppStore,
  projectId: ProjectId,
  personId: PersonId,
  date: IsoDate,
  state: AttendanceState | null,
): Promise<void> {
  await store.write(buildMarkOp(store, projectId, personId, date, state));
}

/** Builds the push op that records or clears one mark, without writing it. */
function buildMarkOp(
  store: AppStore,
  projectId: ProjectId,
  personId: PersonId,
  date: IsoDate,
  state: AttendanceState | null,
): PushOp {
  const id = attendanceMarkId(personId, date);
  const existing = store.replica.attendanceMarks.get(id);
  const now = Date.now();
  const record: AttendanceMark = {
    id,
    projectId,
    personId,
    date,
    // A cleared slot keeps its last state so the record stays well-typed; `deletedAt` is what
    // makes it read as unset.
    state: state ?? existing?.state ?? AttendanceState.Attending,
    updatedBy: store.identity?.email ?? '',
    updatedAt: now,
    deletedAt: state === null ? now : null,
  };
  return { kind: EntityKind.AttendanceMark, record };
}

/**
 * Writes what happened on one session, or clears it.
 *
 * @param personId the person the note is about, or `null` for a note about the day itself.
 * @param text the note; blank text soft-deletes the record, which is how a note is removed.
 */
export async function setNote(
  store: AppStore,
  projectId: ProjectId,
  date: IsoDate,
  personId: PersonId | null,
  text: string,
): Promise<void> {
  const trimmed = text.trim();
  const id = noteId(projectId, date, personId);
  const existing = store.replica.notes.get(id);
  if (trimmed === '' && (existing === undefined || existing.deletedAt !== null)) return;
  const now = Date.now();
  const record: Note = {
    id,
    projectId,
    date,
    personId,
    text: trimmed,
    updatedBy: store.identity?.email ?? '',
    updatedAt: now,
    deletedAt: trimmed === '' ? now : null,
  };
  await store.write({ kind: EntityKind.Note, record });
}

/** Sets `state` for every person in `personIds` on one date, as a single batch. */
export async function setMarksForAll(
  store: AppStore,
  projectId: ProjectId,
  personIds: readonly PersonId[],
  date: IsoDate,
  state: AttendanceState | null,
): Promise<void> {
  await store.writeMany(personIds.map((personId) => buildMarkOp(store, projectId, personId, date, state)));
}
